//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Grid, Typography } from '@mui/material'
import { SectionHeader } from '@react/components/common/SectionHeader'
import ViewField from '@react/components/elements/ViewField'
import PoolHoldingsSkeleton from '@react/components/skeletons/PoolHoldingsSkeleton'
import { useOtChainState } from '@react/redux/store'
import ReportDateRangeSelector from '@react/reporting/ReportDateRangePicker'
import { REPORT_FETCH_DESCRIPTOR_TRANSACTION_LIST } from '@react/reporting/ReportDescriptors'
import { ReportExplorer } from '@react/reporting/ReportExplorer'
import { ReportRowGeneratorClass } from '@react/reporting/ReportRowGeneratorClass'
import { EthAddr } from '@shared/SchemaTypes'
import {
  PoolBaseAccountCTO,
  PoolBaseAccountCTOSchema,
  PoolBaseOverviewCTO,
  PoolFlexAccountCTO,
  PoolFlexAccountCTOSchema,
} from '@shared/sharedTypes'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useState } from 'react'

interface FlexPoolHoldingsProps {
  poolFlexAccountCTO: PoolFlexAccountCTO
  poolBaseAccountCTO: PoolBaseAccountCTO
  poolBaseOverviewCTO: PoolBaseOverviewCTO
  poolAddr: EthAddr
  accountAddr: EthAddr
}

export default function FlexPoolHoldings({
  poolFlexAccountCTO,
  poolBaseAccountCTO,
  poolBaseOverviewCTO,
  poolAddr,
  accountAddr,
}: FlexPoolHoldingsProps): React.ReactElement {
  const [, forceRerender] = useState({})
  const {
    chainConfiguration: { providerReadOnly, chainTimestampDelta },
    stageConfiguration: { serverApiBaseUrl, restAPIKey },
  } = useOtChainState()

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().utc().subtract(30, 'day').add(chainTimestampDelta, 'seconds').startOf('day'),
    dayjs().add(chainTimestampDelta, 'seconds').utc(),
  ])
  const [reportRowGeneratorClass, setReportRowGeneratorClass] = useState<ReportRowGeneratorClass | undefined>(undefined)

  // Add loading state check

  useEffect(() => {
    if (poolAddr && providerReadOnly) {
      const _reportRowGeneratorClass = new ReportRowGeneratorClass({
        reportDescriptor: REPORT_FETCH_DESCRIPTOR_TRANSACTION_LIST,
        providerReadOnly,
        serverApiBaseUrl,
        restAPIKey,
        poolAddr: poolAddr,
        lenderAddr: accountAddr,
        dateRange,
      })
      setReportRowGeneratorClass(_reportRowGeneratorClass)
      _reportRowGeneratorClass.refreshData().then(() => {
        forceRerender({})
      })
    }
  }, [poolAddr])
  // Add loading state check
  if (!poolFlexAccountCTO || !poolBaseAccountCTO || !poolBaseOverviewCTO) {
    return <PoolHoldingsSkeleton />
  }

  return (
    <Box>
      <Box px={3} py={2} sx={{ flexGrow: 1 }}>
        <SectionHeader>
          <Typography variant={'captionBold'}>My Investment Details</Typography>
        </SectionHeader>

        <Grid container spacing={8} sx={{ padding: '24px', marginTop: '-24px', marginLeft: '0px' }}>
          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.principalEarningInterest}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.interestAccrued}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.maxRedeemRequest}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              toolTip="Available Vault Tokens. (Token Balance minus pending withdraw requests)"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.assetsDeposited}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolBaseAccountCTO}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              fieldSchema={PoolBaseAccountCTOSchema.describe().fields.tokenBalance}
              toolTip="The number of vault tokens you currently hold."
            />
          </Grid>
        </Grid>

        <SectionHeader>
          <Typography variant={'captionBold'}>Vault Transactions</Typography>
        </SectionHeader>

        <Grid container spacing={8} sx={{ padding: '24px', marginTop: '-24px', marginLeft: '0px' }}>
          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.requestedAssetsOf}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.maxWithdrawRequest}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.assetsDueForWithdraws}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.assetsWithdrawn}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>
        </Grid>

        <Box sx={{ marginTop: 2 }}>
          <ReportDateRangeSelector
            selectedPoolAddrs={poolAddr ? [poolAddr] : []}
            onSelectedChange={(_dayRange: [Dayjs, Dayjs]) => {
              setDateRange(_dayRange)
              reportRowGeneratorClass?.refreshData().then(() => {
                forceRerender({})
              })
            }}
            forEvents={true}
            initialDateRange={dateRange}
          />
          {reportRowGeneratorClass && <ReportExplorer reportRowGeneratorClass={reportRowGeneratorClass} />}
        </Box>
      </Box>
    </Box>
  )
}
